/**
 * `/api/auth/me` の応答を `AuthenticatedUser` として受け入れてよいか検査する（Issue #8076）。
 *
 * ## なぜ要るのか
 *
 * `AuthProvider` は応答を `as AuthenticatedUser` でキャストして state に入れていた。
 * 一方 `/api/auth/me` には**必須フィールドを欠いたフォールバック応答**がある
 * （`adapter.getCurrentUser()` が null のとき、`status` も `permissions` も無い形を返す）。
 * `data.user` は truthy なので保存され、壊れた値がそのまま消費側へ流れる。
 *
 * 実害は管理ダッシュボード。`useAdminData` は `authUser.permissions.canManageOrganization` を
 * 素で読むので、`permissions` が無いと `TypeError` で画面が落ちる。
 *
 * ## 何を必須にするか
 *
 * **消費側が「在る」前提で読む物だけ**を見る。`AuthenticatedUser` の全フィールドを
 * 検査しない ── optional が多く、厳しくすると正当な応答まで弾いて、
 * 「壊れた値を入れる」より困る「ログインできない」を作ってしまう。
 *
 * - `status` — `OnboardingUser` / `EstablishedUser` の判別子。これが無いと型ガードが効かない
 * - `permissions` — 上記の実害。**キーの有無は見ないが、値が `boolean` であることは見る。**
 *   `{ canManageOrganization: 'false' }` のような文字列は truthy に評価され、
 *   管理画面・組織設定・ページ編集など**各所の表示ガードをすり抜ける**
 * - `id` / `email` — 表示と識別に使う最低限
 */
import type { AuthenticatedUser } from '@/lib/auth/types';

/**
 * 検証の結果。
 *
 * **ログはここで出さない**（Issue #8076 レビュー指摘）。`src/lib/**` は
 * 「純粋関数ベース（副作用は境界層で）」が原則なので、落としたキーは**返すだけ**にして、
 * 記録は呼び出し境界（`AuthProvider` の `readAuthenticatedUser`）が `operation` /
 * `endpoint` / `userId` と一緒に行う。
 */
export interface ParsedAuthenticatedUser {
  /** 受け入れられなければ `null`。 */
  user: AuthenticatedUser | null;
  /** `boolean` でないので落とした権限キー。 */
  droppedPermissionKeys: readonly string[];
}

/** 判別子として受け入れる値。`types.ts` の `status` と揃える。 */
const USER_STATUSES = new Set(['onboarding', 'established']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * 受け入れてよければその値を、そうでなければ `null` を返す。
 *
 * **呼び出し側は `null` を「取得できなかった」と同じに扱う。** 初回取得は
 * セッションからの組み立てへ倒し、再取得は state を更新しない（古い値を保つ方が、
 * 壊れた値を入れるより安全）。
 */
export function parseAuthenticatedUser(value: unknown): ParsedAuthenticatedUser {
  if (!isRecord(value)) return REJECTED;
  if (!isNonEmptyString(value.status) || !USER_STATUSES.has(value.status)) return REJECTED;
  if (!isNonEmptyString(value.id) || !isNonEmptyString(value.email)) return REJECTED;
  if (!isRecord(value.permissions)) return REJECTED;

  const dropped = Object.keys(value.permissions).filter(
    (key) => typeof (value.permissions as Record<string, unknown>)[key] !== 'boolean',
  );
  if (dropped.length === 0) {
    return { user: value as unknown as AuthenticatedUser, droppedPermissionKeys: [] };
  }
  return {
    user: { ...value, permissions: sanitizePermissions(value.permissions) } as unknown as AuthenticatedUser,
    droppedPermissionKeys: dropped,
  };
}

const REJECTED: ParsedAuthenticatedUser = { user: null, droppedPermissionKeys: [] };

/**
 * `boolean` でない権限値を**落とす**（Issue #8076 レビュー指摘）。
 *
 * `{ canManageOrganization: 'false' }` のような文字列は truthy に評価され、表示ガードを
 * すり抜ける。一方 **1 つでも型が違えばユーザーごと弾く**、という形は採らない。
 * 将来サーバが `permissions` に `boolean` 以外のキーを足しただけで**ログインできなくなる**
 * ── この検証が避けたかった方の害が出る。
 *
 * 落とした値は `undefined` になり、消費側では falsy に倒れる（許可しない側）。
 */
function sanitizePermissions(permissions: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(permissions).filter(([, permission]) => typeof permission === 'boolean'),
  );
}
